import React from 'react'
import { Box, Skeleton } from '@mui/material'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
} from 'recharts'
import { format, parseISO } from 'date-fns'
import { tr } from 'date-fns/locale'

const RevenueChart = ({ data = [], isLoading = false }) => {
  // Format data for chart
  const chartData = data.map(item => ({
    ...item,
    date: item.date,
    formattedDate: format(parseISO(item.date), 'dd MMM', { locale: tr }),
    revenue: parseFloat(item.revenue || 0),
    transactions: parseInt(item.transactions || 0),
  }))

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      // Find the original data item to get the correct date
      const dataItem = chartData.find(item => item.formattedDate === label)
      const dateLabel = dataItem ? 
        format(parseISO(dataItem.date), 'dd MMMM yyyy', { locale: tr }) : 
        label
        
      return (
        <Box
          sx={{
            backgroundColor: 'white',
            border: '1px solid #e0e0e0',
            borderRadius: 2,
            padding: 2,
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          }}
        >
          <Box sx={{ fontWeight: 600, mb: 1, color: '#333' }}>
            {dateLabel}
          </Box>
          {payload.map((entry, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                color: entry.color,
                fontSize: '0.875rem',
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: entry.color,
                }}
              />
              <span>
                {entry.name === 'revenue' ? 'Gelir' : 'İşlem Sayısı'}: {' '}
                {entry.name === 'revenue' 
                  ? `$${entry.value.toFixed(2)}` 
                  : entry.value
                }
              </span>
            </Box>
          ))}
        </Box>
      )
    }
    return null
  }

  if (isLoading) {
    return (
      <Box sx={{ width: '100%', height: 450 }}>
        <Skeleton variant="rectangular" width="100%" height={450} />
      </Box>
    )
  }

  if (!chartData.length) {
    return (
      <Box 
        sx={{ 
          height: 450, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          color: 'text.secondary'
        }}
      >
        Grafik verileri yükleniyor...
      </Box>
    )
  }

  return (
    <Box sx={{ width: '100%', height: 450 }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={chartData}
          margin={{
            top: 10,
            right: 30,
            left: 0,
            bottom: 0,
          }}
        >
          <defs>
            <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#2196f3" stopOpacity={0.3}/>
              <stop offset="95%" stopColor="#2196f3" stopOpacity={0.05}/>
            </linearGradient>
          </defs>
          
          <CartesianGrid 
            strokeDasharray="3 3" 
            stroke="#f0f0f0"
            vertical={false}
          />
          
          <XAxis
            dataKey="formattedDate"
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12, fill: '#666' }}
          />
          
          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12, fill: '#666' }}
            tickFormatter={(value) => `$${value.toFixed(2)}`}
          />
          
          <Tooltip content={<CustomTooltip />} />
          
          <Area
            type="monotone"
            dataKey="revenue"
            stroke="#2196f3"
            strokeWidth={3}
            fill="url(#revenueGradient)"
            name="revenue"
          />
        </AreaChart>
      </ResponsiveContainer>
    </Box>
  )
}

export default RevenueChart